/**
 * The single source of truth for screen formats.
 *
 * Warning: These values are duplicated once, in `src/styles/theme.css`
 * (`--breakpoint-*`), because Tailwind cannot read a TS module.
 * `breakpoints.test.ts` fails if the two drift — which is what stops the JS
 * hook and the CSS classes switching at different widths, a bug all but
 * invisible in review.
 */
export const BREAKPOINTS = {
  tablet: 640,
  desktop: 1024,
  /**
   * Where the navigation rail, and with it the three-column shells, begin.
   *
   * Warning: Raised to 1680 after trying 1280: the rail (220), the filter column
   * (246) and the overview column (300) take **766 px before the first game**.
   * On a 1600 px screen only 834 px of content were left, and the screen felt
   * cramped while the window was not. 1680 is the width the mockup draws its
   * wide format at, so its "≥ 1600 px" annotation reads as a floor rather than
   * the switching threshold.
   *
   * Below it the full-width top bar takes over, and the content gains the
   * rail's 220 px.
   */
  wide: 1680,
} as const;

export const FORMATS = ['mobile', 'tablet', 'desktop', 'wide'] as const;
export type Format = (typeof FORMATS)[number];

/**
 * Tells whether a format is at least as wide as another.
 *
 * Avoids long comparisons like `format === 'desktop' || format === 'wide'`,
 * which silently become wrong the day a format is inserted in the middle of the
 * scale.
 *
 * @param format The current format.
 * @param minimum The floor to compare against.
 * @returns `true` when `format` is at or above `minimum`.
 */
export const isAtLeast = (format: Format, minimum: Format): boolean =>
  FORMATS.indexOf(format) >= FORMATS.indexOf(minimum);

/** The possible navigation structures (see the `-formats` mockups in `design/mockups/app`). */
export type NavLayout = 'tabbar' | 'topbar' | 'rail';

/**
 * THE decision point of the navigation's responsive behaviour.
 *
 * `tablet: 'tabbar'` embodies the "a tablet is a widened phone" call, no tablet
 * mockup existing. The mockups annotate the desktop format "≈ 760–1440 px" and
 * therefore suggest `topbar` from ~760 px: the day that is decided otherwise,
 * it changes HERE and nowhere else.
 */
export const NAV_LAYOUT_BY_FORMAT = {
  mobile: 'tabbar',
  tablet: 'tabbar',
  desktop: 'topbar',
  wide: 'rail',
} as const satisfies Record<Format, NavLayout>;
